import type { OrderProblemResponse } from '@/core/generated/api';
import type { ColumnConfig } from '@/shared/ui/DataTable/DataTable';
import type { KeyValueDataObject } from '@/shared/ui/KeyValueRow/KeyValueRow';
import { CopyableValue } from '@/shared/ui/CopyableValue/CopyableValue';
import { formatDateTime, formatOptionalText } from '@/core/format';
import classes from './order-problem.module.scss';

/** Колонки списка проблемных заказов */
export const ORDER_PROBLEM_COLUMNS: ColumnConfig<OrderProblemResponse>[] = [
    {
        field: 'isResolved',
        header: 'Проблема',
        body: (row: OrderProblemResponse) => row.isResolved
            ? 'Устранена'
            : <span
                className={classes.warning}
                aria-label="Активная проблема"
            >
                !
            </span>,
    },
    {
        field: 'externalId',
        header: 'InvId Robokassa',
        body: (row: OrderProblemResponse) => formatOptionalText(row.externalId),
    },
    {
        field: 'orderId',
        header: 'Заказ',
        body: (row: OrderProblemResponse) => formatOptionalText(row.orderId),
    },
    {
        field: 'kind.name',
        header: 'Где возникла проблема',
    },
    {
        field: 'errorMessage',
        header: 'Ошибка',
    },
    {
        field: 'confirmedAmount',
        header: 'Сумма подтверждения',
        body: (row: OrderProblemResponse) => row.confirmedAmount == null
            ? '—'
            : `${row.confirmedAmount.toLocaleString('ru-RU')} ₽`,
    },
    {
        field: 'attempts',
        header: 'Попыток',
    },
    {
        field: 'lastOccurredAt',
        dataType: 'date',
        header: 'Последняя ошибка',
        body: (row: OrderProblemResponse) => formatDateTime(row.lastOccurredAt),
    },
];

/** Подготавливает сведения о проблеме заказа */
export const getOrderProblemDetails = (problem: OrderProblemResponse): KeyValueDataObject[] => [
    {
        label: 'Статус проблемы',
        value: problem.isResolved
            ? 'Устранена'
            : 'Требует внимания',
    },
    {
        label: 'Причина',
        value: problem.errorMessage,
    },
    {
        label: 'Код ошибки',
        value: problem.errorCode,
    },
    {
        label: 'InvId Robokassa',
        value: <CopyableValue value={problem.externalId ?? 'Не найден'} />,
    },
    {
        label: 'ID заказа',
        value: <CopyableValue value={problem.orderId ?? 'Не найден'} />,
    },
    {
        label: 'ID платежа',
        value: <CopyableValue value={problem.paymentId ?? 'Не найден'} />,
    },
    {
        label: 'ID клиента',
        value: <CopyableValue value={problem.clientId ?? 'Не найден'} />,
    },
    {
        label: 'Идентификатор запроса в логах',
        value: <CopyableValue value={problem.correlationId ?? 'Не указан'} />,
    },
    {
        label: 'Сумма подтверждения',
        value: problem.confirmedAmount == null
            ? '—'
            : `${problem.confirmedAmount.toLocaleString('ru-RU')} ₽`,
    },
    {
        label: 'Операция',
        value: problem.source,
    },
    {
        label: 'Запрос',
        value: `${problem.requestMethod ?? ''} ${problem.requestPath ?? ''}`.trim() || 'Фоновая обработка',
    },
    {
        label: 'Зарегистрировано ошибок',
        value: problem.attempts,
    },
    {
        label: 'Первое появление',
        value: formatDateTime(problem.created),
    },
    {
        label: 'Последняя ошибка',
        value: formatDateTime(problem.lastOccurredAt),
    },
    {
        label: 'Следующая попытка',
        value: problem.isResolved
            ? 'Обработка завершена'
            : formatDateTime(problem.nextAttemptAt),
    },
    {
        label: 'Устранено',
        value: formatDateTime(problem.resolvedAt),
    },
];

/** Подготавливает исходные диагностические данные для просмотра */
export const formatDiagnosticPayload = (payload: string): string => {
    try {
        return JSON.stringify(JSON.parse(payload) as unknown, null, 2);
    } catch {
        return payload;
    }
};
